import { useState, useEffect } from "react";
import React from "react";
import { Routes, Route, Navigate, useLocation, Outlet } from "react-router-dom";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { LanguageProvider } from "./i18n/LanguageProvider";
import Index from "./pages/Index";
import WhichPokemonAmI from "./pages/WhichPokemonAmI";
import AboutUs from "./pages/AboutUs";
import ContactUs from "./pages/ContactUs";
import PrivacyPolicy from "./pages/PrivacyPolicy";
import PokemonRandomizerPage from "./pages/PokemonRandomizerPage";
import PokedexPage from "./pages/PokedexPage";
import TypeChartPage from "./pages/TypeChartPage";
import NotFound from "./pages/NotFound";
import ErrorBoundary from "./components/ErrorBoundary";
import { SUPPORTED_LANGUAGES, DEFAULT_LANGUAGE, SupportedLanguage } from "./services/languageService";
import { seoTranslations } from "./i18n/translations/seo";
import { initializeLanguageListener, cleanupExpiredCache, loadLocalPokemonDb } from "./services/pokeApi";
import { createBrowserRouter, RouterProvider, createRoutesFromElements } from "react-router-dom";

// 创建查询客户端
const queryClient = new QueryClient();

/**
 * 检查语言代码是否有效
 */
const isValidLanguage = (lang: string): lang is SupportedLanguage => {
  return SUPPORTED_LANGUAGES.some((l) => l.code === lang);
};

// Define a type for the page keys within seoTranslations
// This extracts the keys like "default", "whichPokemonAmI", etc.
// Assumes 'en' has all possible page keys.
type SeoPageKey = keyof typeof seoTranslations.en;

/**
 * 设置页面标题和元数据
 */
const updateSEO = (lang: SupportedLanguage = DEFAULT_LANGUAGE, pageKey: SeoPageKey = "default") => {
  const langSeoPages = seoTranslations[lang] || seoTranslations[DEFAULT_LANGUAGE];
  const seo = langSeoPages[pageKey] || langSeoPages.default; // Fallback to default SEO for the language
  
  document.title = seo.title;
  
  let metaDescription = document.querySelector('meta[name="description"]');
  if (!metaDescription) {
    metaDescription = document.createElement('meta');
    metaDescription.setAttribute('name', 'description');
    document.head.appendChild(metaDescription);
  }
  metaDescription.setAttribute('content', seo.description);

  // Update Open Graph tags
  let ogTitle = document.querySelector('meta[property="og:title"]');
  if(!ogTitle) {
      ogTitle = document.createElement('meta');
      ogTitle.setAttribute('property', 'og:title');
      document.head.appendChild(ogTitle);
  }
  ogTitle.setAttribute('content', seo.title);

  let ogDescription = document.querySelector('meta[property="og:description"]');
   if(!ogDescription) {
      ogDescription = document.createElement('meta');
      ogDescription.setAttribute('property', 'og:description');
      document.head.appendChild(ogDescription);
  }
  ogDescription.setAttribute('content', seo.description);
  
  // Update other relevant meta tags if needed (e.g., og:image, twitter:card)
};

/**
 * 语言重定向组件
 */
const LanguageRedirect = () => {
  const location = useLocation();
  const pathParts = location.pathname.split('/').filter(Boolean);
  const potentialLangCode = pathParts[0];

  // Redirect /index or other invalid top-level paths to root
  if (!isValidLanguage(potentialLangCode) && SUPPORTED_LANGUAGES.map(l=>l.code).includes(potentialLangCode) === false ) {     
       const actualPath = location.pathname.substring(potentialLangCode.length + 1); // remove the invalid part
       return <Navigate to={actualPath || '/'} replace />;
  }
  
  // If it looks like a language code but isn't supported, go to 404 or default lang?
  // Current logic seems okay, relies on the catch-all * route

  return <Outlet />; // Should render nested route or NotFound if no nested match
};

/**
 * 根布局组件
 */
const RootLayout = () => {
  const location = useLocation();

  useEffect(() => {
    // 尝试加载本地数据库
    loadLocalPokemonDb().then(loaded => {
      if (loaded) {
        console.debug('📚 本地宝可梦数据库已加载');
      } else {
        console.error('❌ 本地宝可梦数据库加载失败');
        // 可以在这里添加额外的错误处理或用户提示
      }
    });

    try {
      initializeLanguageListener();
      console.debug('✅ 语言监听器初始化成功');
    } catch (error) {
      console.error('❌ 语言监听器初始化失败:', error);
    }
    
    try {
      cleanupExpiredCache();
      console.debug('✅ 初始缓存清理成功');
    } catch (error) {
      console.error('❌ 初始缓存清理失败:', error);
    }
    
    const cacheCleanupInterval = setInterval(() => {
      try {
        console.debug('🕒 执行定期缓存清理...');
        cleanupExpiredCache();
      } catch (error) {
        console.error('❌ 定期缓存清理失败:', error);
      }
    }, 6 * 60 * 60 * 1000);
    
    return () => clearInterval(cacheCleanupInterval);
  }, []);

  useEffect(() => {
    const pathParts = location.pathname.split('/').filter(Boolean);
    const firstPart = pathParts[0];
    
    let currentLang: SupportedLanguage = DEFAULT_LANGUAGE;
    let pagePath = '';

    if (isValidLanguage(firstPart)) {
      currentLang = firstPart;
      pagePath = pathParts.length > 1 ? pathParts.slice(1).join('/') : 'index'; // if lang is present, path starts after it
    } else {
      // No valid lang prefix, assume default lang and the whole path is the page path
      pagePath = pathParts.join('/') || 'index';
    }
    
    let pageKey: SeoPageKey = 'default';
    if (pagePath === 'index' || pagePath === '/' || pagePath === '') pageKey = 'default'; // Handle homepage cases
    else if (pagePath === 'which-pokemon-am-i') pageKey = 'whichPokemonAmI';
    else if (pagePath === 'pokemon-randomizer') pageKey = 'pokemonRandomizer';
    else if (pagePath === 'about-us') pageKey = 'aboutUs';
    else if (pagePath === 'contact-us') pageKey = 'contactUs';
    else if (pagePath === 'privacy-policy') pageKey = 'privacyPolicy';
    // If pagePath doesn't match known pages, it will use 'default' SEO from above,
    // or the NotFound page will render which has its own generic title.

    updateSEO(currentLang, pageKey);

  }, [location.pathname]);

  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <TooltipProvider>
          <LanguageProvider>
            <Outlet />
            <Toaster />
          </LanguageProvider>
        </TooltipProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
};

// 创建路由器
const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RootLayout />}>
      {/* 英语路由（默认） */}
      <Route path="/">
        <Route index element={<Index />} />
        <Route path="which-pokemon-am-i" element={<WhichPokemonAmI />} />
        <Route path="about-us" element={<AboutUs />} />
        <Route path="contact-us" element={<ContactUs />} />
        <Route path="privacy-policy" element={<PrivacyPolicy />} />
        <Route path="pokemon-randomizer" element={<PokemonRandomizerPage />} />
        <Route path="pokedex" element={<PokedexPage />} />
        <Route path="type-charts" element={<TypeChartPage />} />
      </Route>

      {/* 其他语言路由 */}
      {SUPPORTED_LANGUAGES.filter(lang => lang.code !== 'en').map((lang) => (
        <Route key={lang.code} path={`/${lang.code}`}>
          <Route index element={<Index />} />
          <Route path="which-pokemon-am-i" element={<WhichPokemonAmI />} />
          <Route path="about-us" element={<AboutUs />} />
          <Route path="contact-us" element={<ContactUs />} />
          <Route path="privacy-policy" element={<PrivacyPolicy />} />
          <Route path="pokemon-randomizer" element={<PokemonRandomizerPage />} />
          <Route path="pokedex" element={<PokedexPage />} />
          <Route path="type-charts" element={<TypeChartPage />} />
        </Route>
      ))}

      {/* 处理无效语言路径 */}
      <Route path="/:lang" element={<LanguageRedirect />} />
      
      {/* 404页面 */}
      <Route path="*" element={<NotFound />} />
    </Route>
  ),
  {
    basename: "/",
    future: {
      v7_startTransition: true,
      v7_relativeSplatPath: true
    } as Record<string, boolean>
  }
);

/**
 * 主应用组件
 */
const App = () => {
  console.debug('🚀 应用启动，已启用React Router v7未来标志');
  return <RouterProvider router={router} />;
};

export default App;
